import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Typography,
  makeStyles,
  CircularProgress,
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { socketConnection } from "../../services/socket";
import { getBackendUrl } from "../../config";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../ConfirmationModal";
import { ChatModal } from "../../pages/Chat";
import InternalChatListItem, { getChatTitle, getUnread } from "./InternalChatListItem";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    overflowY: "auto",
    height: "100%",
    backgroundColor: "transparent",
    borderRadius: 0,
    padding: theme.spacing(0.5),
  },
  empty: {
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  loadingWrap: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3),
  },
  sectionTitle: {
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    padding: theme.spacing(1, 1, 0.5),
  },
  hitItem: {
    borderRadius: 10,
    marginBottom: 4,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&.Mui-selected": {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(25, 118, 210, 0.2)"
          : "rgba(25, 118, 210, 0.12)",
    },
  },
  hitChatName: {
    fontWeight: 700,
    fontSize: "0.8rem",
  },
  hitSnippet: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  hitMark: {
    backgroundColor: "#ffe082",
    color: theme.palette.text.primary,
    borderRadius: 3,
    padding: "0 2px",
    fontWeight: 700,
  },
}));

const createSortFn = (sortOrderMode, sortDir) => (a, b) => {
  const unreadA = a.__unread || 0;
  const unreadB = b.__unread || 0;

  if (sortOrderMode !== "byDate") {
    if (unreadA > 0 && unreadB === 0) return -1;
    if (unreadB > 0 && unreadA === 0) return 1;
  }

  const dateA = new Date(a.updatedAt || 0).getTime();
  const dateB = new Date(b.updatedAt || 0).getTime();
  return sortDir === "ASC" ? dateA - dateB : dateB - dateA;
};

const extractLastMessageText = (chat) => {
  if (!chat?.lastMessage) return "";
  if (typeof chat.lastMessage === "string") return chat.lastMessage;
  if (chat.lastMessage.isDeleted) return "";
  if (chat.lastMessage.message) return String(chat.lastMessage.message);
  if (chat.lastMessage.mediaName) return String(chat.lastMessage.mediaName);
  return "";
};

const chatMatchesSearch = (chat, term, loggedInUser) => {
  if (!term) return true;

  const title = String(getChatTitle(chat, loggedInUser) || "").toLowerCase();
  if (title.includes(term)) return true;

  if (String(chat.title || "").toLowerCase().includes(term)) return true;

  const last = extractLastMessageText(chat).toLowerCase();
  if (last.includes(term)) return true;

  const participants = (chat.users || []).some((u) =>
    String(u?.user?.name || "")
      .toLowerCase()
      .includes(term)
  );
  if (participants) return true;

  const messages = Array.isArray(chat.messages) ? chat.messages : [];
  return messages.some((m) =>
    String(m?.message || "")
      .toLowerCase()
      .includes(term)
  );
};

const highlightSnippet = (text, term, markClass) => {
  const raw = String(text || "");
  if (!term) return raw;
  const lower = raw.toLowerCase();
  const idx = lower.indexOf(term.toLowerCase());
  if (idx < 0) return raw;

  const start = Math.max(0, idx - 28);
  const end = Math.min(raw.length, idx + term.length + 40);
  const prefix = start > 0 ? "…" : "";
  const suffix = end < raw.length ? "…" : "";
  const before = raw.slice(start, idx);
  const match = raw.slice(idx, idx + term.length);
  const after = raw.slice(idx + term.length, end);

  return (
    <>
      {prefix}
      {before}
      <mark className={markClass}>{match}</mark>
      {after}
      {suffix}
    </>
  );
};

const isGroupChat = (chat) => chat?.isGroup === true || chat?.isGroup === "true";

const InternalChatTicketsList = ({
  sortOrderMode = "unreadFirst",
  sortTickets = "DESC",
  searchParam = "",
  selectedUsers = [],
  chatTypeFilter = "all",
  selectedChatId,
  selectedMessageId,
  onSelectChat,
  onSelectSearchHit,
}) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuChat, setMenuChat] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editChat, setEditChat] = useState(null);

  const companyId = user?.companyId;
  const socket = useMemo(() => {
    if (!companyId) return null;
    return socketConnection({ companyId, userId: user?.id });
  }, [companyId, user?.id]);

  const fetchChats = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/chats");
      setChats(Array.isArray(data?.records) ? data.records : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  useEffect(() => {
    if (!socket || !companyId || !user?.id) return undefined;

    const onChatUser = (data) => {
      if (data.action === "create") {
        setChats((prev) => [data.record, ...prev.filter((c) => c.id !== data.record.id)]);
      }
      if (data.action === "update") {
        setChats((prev) =>
          prev.map((chat) =>
            chat.id === data.record.id ? { ...chat, ...data.record } : chat
          )
        );
      }
    };

    const onChat = (data) => {
      if (data.action === "delete") {
        setChats((prev) => prev.filter((c) => c.id !== +data.id));
        if (selectedChatId === +data.id && onSelectChat) {
          onSelectChat(null);
        }
      }
      if (data.action === "new-message" || data.action === "update") {
        if (!data.chat) return;
        setChats((prev) =>
          prev.map((chat) => {
            if (chat.id !== data.chat.id) return chat;
            const incomingUsers = data.chat.users || [];
            const mergedUsers =
              (chat.users || []).length > 0
                ? (chat.users || []).map((u) => {
                    const incoming = incomingUsers.find(
                      (iu) => Number(iu.userId) === Number(u.userId)
                    );
                    return incoming ? { ...u, unreads: incoming.unreads } : u;
                  })
                : incomingUsers;
            return {
              ...chat,
              ...data.chat,
              users: mergedUsers,
              title: data.chat.title ?? chat.title,
              lastMessage: data.chat.lastMessage ?? chat.lastMessage,
              updatedAt: data.chat.updatedAt ?? chat.updatedAt,
            };
          })
        );
      }
    };

    socket.on(`company-${companyId}-chat-user-${user.id}`, onChatUser);
    socket.on(`company-${companyId}-chat`, onChat);
    return () => {
      socket.off(`company-${companyId}-chat-user-${user.id}`, onChatUser);
      socket.off(`company-${companyId}-chat`, onChat);
    };
  }, [socket, companyId, user?.id, selectedChatId, onSelectChat]);

  const filteredSorted = useMemo(() => {
    const term = (searchParam || "").trim().toLowerCase();
    const userIds = (selectedUsers || []).map((u) => Number(u.id || u)).filter(Boolean);

    return chats
      .map((chat) => ({
        ...chat,
        __unread: getUnread(chat, user),
        __title: getChatTitle(chat, user),
        __isGroup: isGroupChat(chat),
      }))
      .filter((chat) => {
        if (chatTypeFilter === "dm" && chat.__isGroup) return false;
        if (chatTypeFilter === "group" && !chat.__isGroup) return false;

        if (term && !chatMatchesSearch(chat, term, user)) {
          return false;
        }

        if (userIds.length > 0) {
          const participantIds = (chat.users || []).map((u) => Number(u.userId));
          const hasAll = userIds.every((id) => participantIds.includes(id));
          if (!hasAll) return false;
        }

        return true;
      })
      .sort(createSortFn(sortOrderMode, sortTickets));
  }, [chats, searchParam, selectedUsers, chatTypeFilter, sortOrderMode, sortTickets, user]);

  const searchHits = useMemo(() => {
    const term = (searchParam || "").trim().toLowerCase();
    if (!term) return [];

    const hits = [];
    filteredSorted.forEach((chat) => {
      const messages = Array.isArray(chat.messages) ? chat.messages : [];
      messages.forEach((message) => {
        const text = String(message?.message || "");
        if (!text.toLowerCase().includes(term)) return;
        hits.push({
          id: `${chat.id}-${message.id}`,
          chat,
          message,
          chatTitle: getChatTitle(chat, user),
          snippet: text,
        });
      });

      if (messages.length === 0) {
        const last = extractLastMessageText(chat);
        if (last.toLowerCase().includes(term) && chat.lastMessage?.id) {
          hits.push({
            id: `${chat.id}-last-${chat.lastMessage.id}`,
            chat,
            message: chat.lastMessage,
            chatTitle: getChatTitle(chat, user),
            snippet: last,
          });
        }
      }
    });

    return hits.sort(
      (a, b) =>
        new Date(b.message?.createdAt || 0).getTime() -
        new Date(a.message?.createdAt || 0).getTime()
    );
  }, [filteredSorted, searchParam, user]);

  const closeMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuClick = (event, chat) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuChat(chat);
  };

  const handleEdit = () => {
    if (!menuChat) return;
    setEditChat(menuChat);
    setEditModalOpen(true);
    closeMenu();
  };

  const handleAskDelete = () => {
    setDeleteConfirmOpen(true);
    closeMenu();
  };

  const handleConfirmDelete = async () => {
    if (!menuChat?.id) return;
    try {
      await api.delete(`/chats/${menuChat.id}`);
      setChats((prev) => prev.filter((c) => c.id !== menuChat.id));
      if (selectedChatId === menuChat.id && onSelectChat) {
        onSelectChat(null);
      }
      toast.success(i18n.t("chatList.deleteSuccess") || "Chat excluído");
    } catch (err) {
      toastError(err);
    } finally {
      setDeleteConfirmOpen(false);
      setMenuChat(null);
    }
  };

  const handleOpenDetails = () => {
    setDetailsOpen(true);
    closeMenu();
  };

  const canEditMenuChat =
    menuChat &&
    Number(menuChat.ownerId) === Number(user?.id) &&
    isGroupChat(menuChat);

  const term = (searchParam || "").trim();
  const hasNoResults = filteredSorted.length === 0 && searchHits.length === 0;

  const dialogs = (
    <>
      <Menu
        anchorEl={menuAnchorEl}
        keepMounted
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
      >
        {canEditMenuChat && (
          <MenuItem onClick={handleEdit}>
            {i18n.t("chatList.edit") || "Editar"}
          </MenuItem>
        )}
        {user?.profile === "admin" && (
          <MenuItem onClick={handleAskDelete}>
            {i18n.t("chatList.delete") || "Excluir"}
          </MenuItem>
        )}
        {menuChat && isGroupChat(menuChat) && (
          <MenuItem onClick={handleOpenDetails}>
            {i18n.t("chatList.details") || "Detalhes"}
          </MenuItem>
        )}
      </Menu>

      <ConfirmationModal
        title={i18n.t("chatList.deleteChat") || "Excluir chat"}
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      >
        {i18n.t("chatList.deleteChatConfirm") ||
          "Tem certeza que deseja excluir este chat?"}
      </ConfirmationModal>

      <ConfirmationModal
        title={i18n.t("chatList.detailsTitle") || "Detalhes do grupo"}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
      >
        <div style={{ padding: 20 }}>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <Avatar
              src={
                menuChat?.groupImage
                  ? String(menuChat.groupImage).startsWith("http")
                    ? menuChat.groupImage
                    : `${getBackendUrl()}${menuChat.groupImage}`
                  : undefined
              }
              style={{ width: 100, height: 100, margin: "0 auto" }}
            />
            <Typography variant="h6" style={{ marginTop: 10 }}>
              {menuChat?.title}
            </Typography>
            {menuChat?.description && (
              <Typography variant="body2" color="textSecondary">
                {menuChat.description}
              </Typography>
            )}
          </div>
          <List>
            {(menuChat?.users || []).map((member) => (
              <ListItem key={member.userId}>
                <ListItemAvatar>
                  <Avatar
                    src={
                      member.user?.profileImage
                        ? `${getBackendUrl()}/public/company${member.user.companyId}/user/${member.user.profileImage}`
                        : undefined
                    }
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={member.user?.name}
                  secondary={
                    Number(member.userId) === Number(menuChat.ownerId)
                      ? i18n.t("chatList.admin") || "Admin"
                      : i18n.t("chatList.member") || "Membro"
                  }
                />
              </ListItem>
            ))}
          </List>
        </div>
      </ConfirmationModal>

      <ChatModal
        type="edit"
        open={editModalOpen}
        chat={editChat || {}}
        handleClose={() => {
          setEditModalOpen(false);
          setEditChat(null);
        }}
        handleLoadNewChat={(updated) => {
          if (!updated?.id) return;
          setChats((prev) =>
            prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c))
          );
          if (selectedChatId === updated.id && onSelectChat) {
            onSelectChat(updated);
          }
          setEditModalOpen(false);
          setEditChat(null);
        }}
        findChats={async () => {
          const { data } = await api.get("/chats");
          const records = Array.isArray(data?.records) ? data.records : [];
          setChats(records);
          return data;
        }}
        setChats={setChats}
        setChatsPageInfo={() => {}}
        chats={chats}
        loggedInUserId={user?.id}
      />
    </>
  );

  if (loading) {
    return (
      <>
        {dialogs}
        <div className={classes.loadingWrap}>
          <CircularProgress size={28} />
        </div>
      </>
    );
  }

  if (hasNoResults) {
    return (
      <>
        {dialogs}
        <Paper square elevation={0} className={classes.root}>
          <Typography className={classes.empty}>
            Nenhum chat interno encontrado.
          </Typography>
        </Paper>
      </>
    );
  }

  return (
    <>
      {dialogs}
      <Paper square elevation={0} className={classes.root}>
        {term && searchHits.length > 0 && (
          <>
            <Typography className={classes.sectionTitle}>
              Mensagens encontradas ({searchHits.length})
            </Typography>
            <List disablePadding>
              {searchHits.map((hit) => (
                <ListItem
                  key={hit.id}
                  dense
                  button
                  selected={
                    selectedChatId === hit.chat.id &&
                    selectedMessageId === hit.message.id
                  }
                  className={classes.hitItem}
                  onClick={() => {
                    if (onSelectSearchHit) {
                      onSelectSearchHit({
                        chat: hit.chat,
                        messageId: hit.message.id,
                      });
                    } else if (onSelectChat) {
                      onSelectChat(hit.chat);
                    }
                  }}
                >
                  <ListItemText
                    disableTypography
                    primary={
                      <Typography className={classes.hitChatName} noWrap>
                        {hit.chatTitle}
                      </Typography>
                    }
                    secondary={
                      <Typography className={classes.hitSnippet} component="div">
                        {highlightSnippet(hit.snippet, term, classes.hitMark)}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}

        <Typography className={classes.sectionTitle}>
          {term ? "Conversas" : "Chat Interno"}
        </Typography>
        <List disablePadding>
          {filteredSorted.map((chat) => (
            <InternalChatListItem
              key={chat.id}
              chat={chat}
              selected={selectedChatId === chat.id && !selectedMessageId}
              onSelect={(selected) => {
                // Zera badge local imediatamente ao abrir (igual /chats)
                setChats((prev) =>
                  prev.map((c) =>
                    c.id === selected.id
                      ? {
                          ...c,
                          users: (c.users || []).map((u) =>
                            Number(u.userId) === Number(user.id)
                              ? { ...u, unreads: 0 }
                              : u
                          ),
                        }
                      : c
                  )
                );
                if (onSelectChat) onSelectChat(selected);
              }}
              onMenuClick={handleMenuClick}
            />
          ))}
        </List>
      </Paper>
    </>
  );
};

export default InternalChatTicketsList;
